import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useForm, type SubmitHandler, type FieldErrors } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { Eye, EyeOff, Loader2, Mail, Lock, Building2 } from 'lucide-react'

import { NetworkErrorToast } from '../../../components/NetworkErrorToast'
import { AuthPageShell, LoginFlowSuccessState } from '../components/AuthShared'
import { getRoleHomePath, getStoredRoleHomePath, isAuthenticated, normalizeStoredRole, type UserRole } from '../../../routes/auth'
import { loginSchema, type LoginFormData } from '../schemas/authSchema'
import { useAuthStore } from '../hooks/useAuthStore'
import { authApi } from '../../../api/auth/auth.api'
import { saveAuthTokens, clearAuthTokens } from '../../../shared/utils/tokenStorage'
import { getActiveAcademicYearId } from '../../../shared/api/academicYear'
import { showToastMessage as showAppToastMessage } from '../../../components/Toast'

const trustedOtpStorageKey = 'school-erp-trusted-otp-session'
const rememberedSessionDays = 30

type TrustedOtpSession = {
  email: string
  expiresAt: number
}

function readTrustedOtpSession() {
  const rawValue = window.localStorage.getItem(trustedOtpStorageKey)
  if (!rawValue) return null

  try {
    const parsed = JSON.parse(rawValue) as TrustedOtpSession
    if (!parsed.email || !parsed.expiresAt) {
      window.localStorage.removeItem(trustedOtpStorageKey)
      return null
    }
    if (parsed.expiresAt <= Date.now()) {
      window.localStorage.removeItem(trustedOtpStorageKey)
      return null
    }
    return parsed
  } catch {
    window.localStorage.removeItem(trustedOtpStorageKey)
    return null
  }
}

function hasActiveTrustedOtpSession(email: string) {
  const session = readTrustedOtpSession()
  return session?.email === email.trim().toLowerCase()
}

function saveTrustedOtpSession(email: string) {
  const expiresAt = Date.now() + rememberedSessionDays * 24 * 60 * 60 * 1000
  const session: TrustedOtpSession = {
    email: email.trim().toLowerCase(),
    expiresAt,
  }
  window.localStorage.setItem(trustedOtpStorageKey, JSON.stringify(session))
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const mockOtp = '1201'
  const otpLength = 4

  const loginZustand = useAuthStore((state) => state.login)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      password: '',
      orgCode: '',
      rememberMe: false,
    },
  })

  const emailValue = watch('email')
  const rememberMeValue = watch('rememberMe')

  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const [isLoading, setIsLoading] = useState(false)
  const [showOtpStep, setShowOtpStep] = useState(false)
  const [showSuccessStep, setShowSuccessStep] = useState(false)
  const [otpValues, setOtpValues] = useState(() => Array.from({ length: otpLength }, () => ''))
  const [otpInvalid, setOtpInvalid] = useState(false)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)

  const [showFirstTimePasswordStep, setShowFirstTimePasswordStep] = useState(false)
  const [firstTimeResetToken, setFirstTimeResetToken] = useState<string | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [firstTimeError, setFirstTimeError] = useState<string | null>(null)
  const [isSubmittingFirstTime, setIsSubmittingFirstTime] = useState(false)

  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/'
  const hasProtectedReturnPath = from !== '/login' && from !== '/'

  const getRoleFromEmail = (email?: string): UserRole => {
    const normalized = (email || '').trim().toLowerCase()
    if (normalized.includes('teacher')) return 'teacher'
    if (normalized.includes('admin')) return 'admin'
    return 'staff'
  }

  const resolvePostLoginPath = (userRole?: UserRole, customPermissions?: string[], rawUserRole?: string) => {
    const role = userRole || getRoleFromEmail(emailValue)
    if (hasProtectedReturnPath && !from.includes('settings') && !from.includes('change-password')) {
      return from
    }
    return getRoleHomePath(role, customPermissions, rawUserRole)
  }

  useEffect(() => {
    if (isAuthenticated()) {
      navigate(hasProtectedReturnPath ? from : getStoredRoleHomePath(), { replace: true })
    }
  }, [from, hasProtectedReturnPath, navigate])

  const onFormInvalid = (formErrors: FieldErrors<LoginFormData>) => {
    if (formErrors.email) {
      showAppToastMessage(formErrors.email.message || 'Oops! Incorrect Mail Id. Please try again.', 'error')
    } else if (formErrors.password) {
      showAppToastMessage(formErrors.password.message || 'Oops! Incorrect Password Please try again.', 'error')
    }
  }

  function extractErrorMessage(err: any): string {
    if (!err) return 'Login failed. Please check your credentials.'

    let rawMsg = ''
    const resData = err?.response?.data

    if (resData) {
      if (typeof resData.message === 'string' && resData.message.trim()) {
        rawMsg = resData.message
      } else if (Array.isArray(resData.message)) {
        rawMsg = resData.message.filter(Boolean).join(', ')
      } else if (typeof resData.error === 'string' && resData.error.trim()) {
        rawMsg = resData.error
      } else if (typeof resData === 'string' && resData.trim()) {
        rawMsg = resData
      }
    }

    if (!rawMsg && typeof err.message === 'string' && err.message.trim()) {
      rawMsg = err.message
    }

    // Handle HTML error pages (e.g. Nginx 502 Bad Gateway, 504 Timeout, 500 Server Error)
    if (rawMsg && (/<[a-z][\s\S]*>/i.test(rawMsg) || rawMsg.includes('<html') || rawMsg.includes('<title>'))) {
      if (rawMsg.includes('502 Bad Gateway')) {
        return '502 Bad Gateway: Server is temporarily unavailable. Please try again later.'
      }
      if (rawMsg.includes('504 Gateway Timeout')) {
        return '504 Gateway Timeout: Server took too long to respond. Please try again later.'
      }
      if (rawMsg.includes('500 Internal Server Error')) {
        return '500 Internal Server Error: Server encountered an error. Please try again later.'
      }
      if (rawMsg.includes('404 Not Found')) {
        return '404 Not Found: Requested endpoint was not found on the server.'
      }
      const titleMatch = rawMsg.match(/<title>(.*?)<\/title>/i)
      if (titleMatch && titleMatch[1]) {
        return titleMatch[1].trim()
      }
      const cleanText = rawMsg.replace(/<[^>]*>?/gm, '').trim()
      if (cleanText) {
        return cleanText.slice(0, 150)
      }
      return 'Server error occurred. Please try again later.'
    }

    const status = err?.response?.status
    if (status === 502) {
      return '502 Bad Gateway: Server is temporarily unavailable. Please try again later.'
    }
    if (status === 504) {
      return '504 Gateway Timeout: Server took too long to respond. Please try again later.'
    }
    if (status === 500) {
      return '500 Internal Server Error: Server encountered an error. Please try again later.'
    }

    return rawMsg || 'Oops! Incorrect email or password. Please try again.'
  }

  const onLoginSubmit: SubmitHandler<LoginFormData> = async (data) => {
    setError(null)
    setIsLoading(true)

    try {
      const response = await authApi.login({
        email: data.email,
        password: data.password,
        organizationCode: data.orgCode,
        orgId: data.orgCode,
        orgCode: data.orgCode,
      })

      const requiresReset =
        response?.requiresPasswordReset ||
        response?.data?.requiresPasswordReset ||
        Boolean(response?.resetToken || response?.data?.resetToken)

      const resetToken =
        response?.resetToken ||
        response?.data?.resetToken ||
        null

      if (requiresReset) {
        setFirstTimeResetToken(resetToken)
        setShowFirstTimePasswordStep(true)
        showAppToastMessage('First-time login detected. Please set your new password.', 'info')
        return
      }

      if (response?.success === false) {
        const errorMsg = response?.message || (response?.data as any)?.message || 'Login failed. Please check your credentials.'
        setError(errorMsg)
        showAppToastMessage(errorMsg, 'error')
        return
      }

      const accessToken =
        response?.accessToken ||
        response?.token ||
        response?.data?.accessToken ||
        response?.data?.token

      const refreshToken =
        response?.refreshToken ||
        response?.data?.refreshToken ||
        (response?.data as any)?.refresh_token ||
        ''

      if (accessToken) {
        saveAuthTokens(accessToken, refreshToken)

        const apiUser = response?.user || response?.data?.user
        const detectedRoleUpper = (
          apiUser?.role ||
          apiUser?.userType ||
          apiUser?.type ||
          apiUser?.assignedRole?.roleName ||
          response?.role ||
          response?.data?.role ||
          ''
        ).toUpperCase();

        if (detectedRoleUpper === 'STUDENT') {
          clearAuthTokens();
          const studentMsg = 'Student accounts do not have access to this portal.';
          setError(studentMsg);
          showAppToastMessage(studentMsg, 'error');
          setIsLoading(false);
          return;
        }
        const rawRole =
          response?.role ||
          response?.data?.role ||
          apiUser?.role ||
          getRoleFromEmail(data.email)
        const apiRole = normalizeStoredRole(rawRole) || (rawRole ? (rawRole.toLowerCase() as UserRole) : 'admin')
        const apiPermissions = response?.permissions || response?.data?.permissions || apiUser?.permissions

        const userMeta = {
          id: apiUser?.id || `user-${Date.now()}`,
          name: apiUser?.fullName || apiUser?.name || (apiUser?.firstName ? `${apiUser.firstName} ${apiUser.lastName || ''}`.trim() : 'User'),
          email: apiUser?.email || data.email,
          role: apiRole,
          rawRole: rawRole,
          organizationId: (response as any)?.organizationId || (response as any)?.data?.organizationId || apiUser?.orgId || apiUser?.organizationId,
          branchId: (response as any)?.branchId || (response as any)?.data?.branchId || (apiUser?.branchIds ? apiUser.branchIds[0] : undefined),
          accessToken: accessToken,
          token: accessToken,
        }

        const normalizedEmail = data.email.trim().toLowerCase()
        const shouldUseOtp = data.rememberMe && !hasActiveTrustedOtpSession(normalizedEmail)

        if (shouldUseOtp) {
          setOtpValues(Array.from({ length: otpLength }, () => ''))
          setOtpInvalid(false)
          setShowSuccessStep(false)
          setShowOtpStep(true)
          return
        }

        let activeAcademicYear =
          (response as any)?.academicYearId ||
          (response as any)?.academicSessionId ||
          (response as any)?.data?.academicYearId ||
          (response as any)?.data?.academicSessionId ||
          (response as any)?.data?.sessionId ||
          apiUser?.academicYearId ||
          apiUser?.academicSessionId ||
          ''

        if (!activeAcademicYear) {
          activeAcademicYear = await getActiveAcademicYearId()
        }

        if (activeAcademicYear) {
          localStorage.setItem('academic_year_id', activeAcademicYear)
          localStorage.setItem('x_academic_year', activeAcademicYear)
          localStorage.setItem('academic_session_id', activeAcademicYear)
        }

        loginZustand(apiRole, apiPermissions, userMeta)
        showAppToastMessage('Login successful! Redirecting to dashboard...', 'success')
        navigate(resolvePostLoginPath(apiRole, apiPermissions, rawRole), { replace: true })
        return
      }

      const fallbackMsg = response?.message || 'Invalid email or password. Please try again.'
      setError(fallbackMsg)
      showAppToastMessage(fallbackMsg, 'error')
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err)
      setError(errorMessage)
      showAppToastMessage(errorMessage, 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleFirstTimePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setFirstTimeError(null)

    if (!newPassword) {
      const msg = 'Please fill out the new password field.'
      setFirstTimeError(msg)
      showAppToastMessage(msg, 'error')
      return
    }
    if (newPassword.length < 8) {
      const msg = 'Password must be at least 8 characters.'
      setFirstTimeError(msg)
      showAppToastMessage(msg, 'error')
      return
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/\d/.test(newPassword)) {
      const msg = 'Password must include uppercase, lowercase, and a number.'
      setFirstTimeError(msg)
      showAppToastMessage(msg, 'error')
      return
    }
    if (newPassword !== confirmPassword) {
      const msg = 'Confirm Password does not match your New Password.'
      setFirstTimeError(msg)
      showAppToastMessage(msg, 'error')
      return
    }

    setIsSubmittingFirstTime(true)
    try {
      if (firstTimeResetToken) {
        await authApi.resetPasswordFirstLogin({
          resetToken: firstTimeResetToken,
          newPassword,
        })
      }
      showAppToastMessage('Password reset successfully! Please log in again with your new password.', 'success')
      setShowFirstTimePasswordStep(false)
      setNewPassword('')
      setConfirmPassword('')
      setFirstTimeResetToken(null)
    } catch (err: any) {
      const errMsg = extractErrorMessage(err) || 'Failed to reset password. Please try again.'
      setFirstTimeError(errMsg)
      showAppToastMessage(errMsg, 'error')
    } finally {
      setIsSubmittingFirstTime(false)
    }
  }

  const handleOtpChange = (index: number, value: string) => {
    const nextValue = value.replace(/\D/g, '').slice(-1)

    setOtpValues((current) => {
      const next = [...current]
      next[index] = nextValue
      return next
    })
    setOtpInvalid(false)

    if (nextValue && index < otpLength - 1) {
      window.requestAnimationFrame(() => {
        const nextInput = document.getElementById(`otp-${index + 1}`) as HTMLInputElement | null
        nextInput?.focus()
      })
    }
  }

  const handleOtpKeyDown = (index: number, key: string) => {
    if (key === 'Backspace' && !otpValues[index] && index > 0) {
      const previousInput = document.getElementById(`otp-${index - 1}`) as HTMLInputElement | null
      previousInput?.focus()
    }
  }

  const handleVerifyOtp = (event: FormEvent) => {
    event.preventDefault()

    if (otpValues.some((value) => !value.trim())) {
      setOtpInvalid(true)
      showAppToastMessage('Please enter the OTP to continue.')
      return
    }

    if (otpValues.join('') !== mockOtp) {
      setOtpInvalid(true)
      showAppToastMessage('Oops! Entered OTP is incorrect. Please try again.')
      return
    }

    setIsVerifyingOtp(true)

    setTimeout(() => {
      setIsVerifyingOtp(false)
      if (rememberMeValue) {
        saveTrustedOtpSession(emailValue)
      }
      setShowOtpStep(false)
      setShowSuccessStep(true)
      showAppToastMessage('OTP Verified! Logged in successfully.', 'success')
    }, 700)
  }

  const handleResendOtp = () => {
    setOtpValues(Array.from({ length: otpLength }, () => ''))
    setOtpInvalid(false)
    showAppToastMessage('A new OTP has been sent successfully.')
    window.requestAnimationFrame(() => {
      const firstInput = document.getElementById('otp-0') as HTMLInputElement | null
      firstInput?.focus()
    })
  }

  return (
    <AuthPageShell>
      <NetworkErrorToast />
      
      <div className="w-full">
        {showFirstTimePasswordStep ? (
          <div className="text-left w-full">
            <h2 className="text-lg font-bold text-gray-900">
              Set New Password
            </h2>
            <p className="text-xs text-gray-500 mt-1 mb-4">
              First time login: Please update your password to secure your account.
            </p>



            <form onSubmit={handleFirstTimePasswordSubmit} className="space-y-4 font-sans">
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-700">
                  New Password
                </label>
                <div className="relative flex items-center">
                  <Lock className="pointer-events-none absolute left-3.5 h-4 w-4 text-gray-400" />
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value)
                      if (firstTimeError) setFirstTimeError(null)
                    }}
                    className="w-full h-[46px] rounded-lg border border-gray-200 bg-white pl-10 pr-10 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#146971]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3.5 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-700">
                  Confirm Password
                </label>
                <div className="relative flex items-center">
                  <Lock className="pointer-events-none absolute left-3.5 h-4 w-4 text-gray-400" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value)
                      if (firstTimeError) setFirstTimeError(null)
                    }}
                    className="w-full h-[46px] rounded-lg border border-gray-200 bg-white pl-10 pr-10 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#146971]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3.5 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600 space-y-1 border border-gray-100">
                <p className="font-semibold text-gray-800">Password Requirements:</p>
                <p className={newPassword.length >= 8 ? 'text-emerald-600 font-medium' : ''}>
                  {newPassword.length >= 8 ? '✓ ' : '• '}Minimum 8 characters
                </p>
                <p className={/[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword) ? 'text-emerald-600 font-medium' : ''}>
                  {/[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword) ? '✓ ' : '• '}Uppercase & lowercase letters
                </p>
                <p className={/\d/.test(newPassword) ? 'text-emerald-600 font-medium' : ''}>
                  {/\d/.test(newPassword) ? '✓ ' : '• '}At least 1 number
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmittingFirstTime}
                className="h-[46px] w-full bg-[#146971] hover:bg-[#0f545a] text-white font-medium py-2.5 rounded-lg text-sm transition-colors shadow-sm flex items-center justify-center cursor-pointer disabled:cursor-not-allowed disabled:opacity-80"
              >
                {isSubmittingFirstTime ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update Password & Continue'}
              </button>
            </form>
          </div>
        ) : showSuccessStep ? (
          <div className="flex w-full flex-col items-center text-center">
            <LoginFlowSuccessState
              imageAlt="Login successful"
              message="Login successful. continue to the"
              linkTo={resolvePostLoginPath()}
              linkLabel="dashboard"
            />
          </div>
        ) : showOtpStep ? (
          <div className="flex w-full flex-col items-center text-center">
            <h2 className="text-lg font-bold text-gray-900">
              Enter OTP
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              We have sent an OTP code to your mobile number/mail id
            </p>

            <form onSubmit={handleVerifyOtp} className="mt-6 w-full">
              <div className="flex items-center justify-center gap-2.5">
                {otpValues.map((value, index) => (
                  <div key={`otp-${index}`} className="flex items-center gap-2.5">
                    <input
                      id={`otp-${index}`}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={1}
                      value={value}
                      onChange={(event) => handleOtpChange(index, event.target.value)}
                      onKeyDown={(event) => handleOtpKeyDown(index, event.key)}
                      className={`h-11 w-12 rounded-lg border bg-white text-center text-base font-semibold text-gray-800 shadow-sm outline-none transition focus:ring-2 ${
                        otpInvalid
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                          : 'border-gray-200 focus:border-[#146971] focus:ring-[#146971]/20'
                      }`}
                    />
                    {index < otpLength - 1 && (
                      <span aria-hidden className="text-gray-300 font-bold">
                        -
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <button
                type="submit"
                disabled={isVerifyingOtp}
                className="mt-6 flex h-[46px] w-full bg-[#146971] hover:bg-[#0f545a] text-white font-medium py-2.5 rounded-lg text-sm transition-colors shadow-sm items-center justify-center disabled:cursor-not-allowed disabled:opacity-80 cursor-pointer"
              >
                {isVerifyingOtp ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify OTP'}
              </button>
            </form>

            <p className="mt-4 flex items-center justify-center gap-1.5 text-xs font-medium text-gray-600">
              <span>Didn’t receive code?</span>
              <button
                type="button"
                onClick={handleResendOtp}
                className="bg-transparent p-0 font-bold text-[#146971] hover:underline cursor-pointer"
              >
                Resend OTP
              </button>
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3 w-full">
            <p className="text-[13px] font-medium text-[#374151] text-left w-full">
              Enter your credentials to access your account
            </p>

            <form onSubmit={handleSubmit(onLoginSubmit, onFormInvalid)} className="flex flex-col gap-3 font-sans">
              {/* Email Field */}
              <div className="flex flex-col gap-1 w-full">
                <label
                  htmlFor="email"
                  className="text-[13px] font-semibold text-[#111827] text-left"
                >
                  Email Address
                </label>
                <div className="relative w-full">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-[16px] h-[16px] text-[#9ca3af]" />
                  <input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    {...register('email')}
                    className={`w-full h-[38px] rounded-lg border bg-white pl-9 pr-3 text-[13px] text-[#111827] placeholder:text-[#9ca3af] transition focus:outline-none focus:ring-2 ${
                      errors.email
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                        : 'border-[#d1d5db] focus:border-[#146971] focus:ring-[#146971]/20'
                    }`}
                  />
                </div>
                {errors.email && (
                  <p className="text-[11px] font-medium text-red-500">{errors.email.message}</p>
                )}
              </div>

              {/* Password Field */}
              <div className="flex flex-col gap-1 w-full">
                <label
                  htmlFor="password"
                  className="text-[13px] font-semibold text-[#111827] text-left"
                >
                  Password
                </label>
                <div className="relative w-full">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-[16px] h-[16px] text-[#9ca3af]" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    {...register('password')}
                    className={`w-full h-[38px] rounded-lg border bg-white pl-9 pr-9 text-[13px] text-[#111827] placeholder:text-[#9ca3af] transition focus:outline-none focus:ring-2 ${
                      errors.password
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                        : 'border-[#d1d5db] focus:border-[#146971] focus:ring-[#146971]/20'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b7280] hover:text-[#111827]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-[16px] h-[16px]" /> : <Eye className="w-[16px] h-[16px]" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-[11px] font-medium text-red-500">{errors.password.message}</p>
                )}
                <div className="flex justify-end w-full mt-0.5">
                  <Link
                    to="/forgot-password"
                    className="text-[12px] font-medium text-[#146971] hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>

              {/* Organization Code Field */}
              <div className="flex flex-col gap-1 w-full">
                <label
                  htmlFor="orgCode"
                  className="text-[13px] font-semibold text-[#111827] text-left"
                >
                  Organization Code 
                </label>
                <div className="relative w-full">
                  <Building2 className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-[16px] h-[16px] text-[#9ca3af]" />
                  <input
                    id="orgCode"
                    type="text"
                    placeholder="Enter ORG Code if applicable"
                    {...register('orgCode')}
                    className="w-full h-[38px] rounded-lg border border-[#d1d5db] bg-white pl-9 pr-3 text-[13px] text-[#111827] placeholder:text-[#9ca3af] transition focus:outline-none focus:ring-2 focus:border-[#146971] focus:ring-[#146971]/20"
                  />
                </div>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="h-[40px] w-full bg-[#146971] hover:bg-[#0f5259] active:bg-[#0b3e43] text-white text-[14px] font-semibold rounded-lg transition-colors shadow-sm flex items-center justify-center mt-1 disabled:cursor-not-allowed disabled:opacity-80 cursor-pointer"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Login'}
              </button>
            </form>
          </div>
        )}
      </div>
    </AuthPageShell>
  )
}
